import React, { useState, useEffect, useCallback } from 'react';
import { useData } from '../context/DataContext';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, UserPlus, Shield, Mail, Calendar, Mic, Trophy, Star, Clock, ChevronRight, CircleCheck as CheckCircle, Circle as XCircle, Trash2, TriangleAlert as AlertTriangle, Coins, Download, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, User, Bell, Send, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { VirtualCurrencyService } from '../services/VirtualCurrencyService';
import { useUser } from '../context/UserContext';

interface User {
  id: string;
  email: string;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
  role: 'user' | 'admin';
  created_at: string;
  last_login?: string;
  recordings_count?: number;
  tournaments_count?: number;
  achievements_count?: number;
}

type SortField = 'username' | 'email' | 'created_at';
type SortDirection = 'asc' | 'desc';

const PAGE_SIZE = 50;

export const AdminUsers: React.FC = () => {
  const { adminService } = useData();
  const { user: currentUser } = useUser();
  const navigate = useNavigate();
  const coinService = new VirtualCurrencyService();

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUserDetails, setShowUserDetails] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<Partial<User> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [showGiftCoinsModal, setShowGiftCoinsModal] = useState(false);
  const [giftAmount, setGiftAmount] = useState(0);
  const [giftNote, setGiftNote] = useState('');
  const [giftType, setGiftType] = useState<'gift' | 'reload' | 'promotion' | 'compensation'>('gift');
  const [giftingCoins, setGiftingCoins] = useState(false);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [showPushModal, setShowPushModal] = useState(false);
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushAudience, setPushAudience] = useState<'admins' | 'all'>('admins');
  const [sendingPush, setSendingPush] = useState(false);
  const [pushResult, setPushResult] = useState<{ sent: number; recipients: number } | null>(null);
  const [pushError, setPushError] = useState<string | null>(null);

  const handleSendPush = async () => {
    setPushError(null);
    setPushResult(null);
    const title = pushTitle.trim();
    const body = pushBody.trim();
    if (!title || !body) {
      setPushError('Title and message are required');
      return;
    }
    try {
      setSendingPush(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-push-notification`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ title, body, audience: pushAudience }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to send push');
      setPushResult({ sent: json.sent ?? 0, recipients: json.recipients ?? 0 });
    } catch (err) {
      setPushError(err instanceof Error ? err.message : 'Failed to send push');
    } finally {
      setSendingPush(false);
    }
  };

  const closePushModal = () => {
    setShowPushModal(false);
    setPushTitle('');
    setPushBody('');
    setPushAudience('admins');
    setPushResult(null);
    setPushError(null);
  };

  useEffect(() => {
    loadAllUsers();
  }, []);

  const loadAllUsers = async () => {
    try {
      setLoading(true);
      const allUsers: User[] = [];
      let offset = 0;
      const batchSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error: rpcError } = await supabase
          .rpc('load_users')
          .range(offset, offset + batchSize - 1);

        if (rpcError) {
          throw new Error(rpcError.message);
        }

        if (data && data.length > 0) {
          allUsers.push(...data);
          offset += data.length;
          hasMore = data.length === batchSize;
        } else {
          hasMore = false;
        }
      }

      setUsers(allUsers);
    } catch (error) {
      setError('Failed to load users');
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'created_at' ? 'desc' : 'asc');
    }
    setCurrentPage(1);
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpDown size={14} className="ml-1 text-white/30" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp size={14} className="ml-1 text-cyan-400" />
      : <ArrowDown size={14} className="ml-1 text-cyan-400" />;
  };

  const validateUserData = (userData: Partial<User>) => {
    const errors: {[key: string]: string} = {};
    
    if (!userData.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!userData.username?.trim()) {
      errors.username = 'Username is required';
    }
    
    if (!userData.full_name?.trim()) {
      errors.full_name = 'Full name is required';
    } else if (!userData.full_name.includes(' ')) {
      errors.full_name = 'Please enter both first and last name';
    }
    
    return errors;
  };

  const handleUpdateRole = async (userId: string, newRole: 'user' | 'admin') => {
    try {
      await adminService.updateUserRole(userId, newRole);
      setUsers(users.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));
      setSuccess('User role updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError('Failed to update user role');
      console.error('Error updating user role:', error);
    }
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    
    try {
      // Clear previous errors
      setError(null);
      setValidationErrors({});
      
      // Validate user data
      const errors = validateUserData(editingUser);
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
      }

      if (editingUser.id) {
        await adminService.updateUser(editingUser.id, editingUser);
      } else {
        await adminService.createUser({
          email: editingUser.email!,
          username: editingUser.username!,
          full_name: editingUser.full_name!,
          role: editingUser.role || 'user'
        });
      }
      
      await loadAllUsers();
      setEditingUser(null);
      setSuccess(editingUser.id ? 'User updated successfully' : 'User created successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes('email or username may already be in use')) {
          setValidationErrors({
            duplicate: 'This email or username is already in use. Please try different ones.'
          });
        } else {
          setError(error.message);
        }
      } else {
        setError('An unexpected error occurred');
      }
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setIsDeleting(true);
      await adminService.deleteUser(userToDelete.id);
      await loadAllUsers();
      setUserToDelete(null);
      setSuccess('User deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError('Failed to delete user');
      console.error('Error deleting user:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleGiftCoins = async () => {
    if (!showUserDetails || !currentUser || giftAmount <= 0) return;

    try {
      setGiftingCoins(true);
      await coinService.giftCoins(
        showUserDetails,
        giftAmount,
        currentUser.id,
        giftType,
        giftNote || undefined
      );
      setSuccess(`Successfully gifted ${giftAmount} coins!`);
      setShowGiftCoinsModal(false);
      setGiftAmount(0);
      setGiftNote('');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error gifting coins:', err);
      setError(err instanceof Error ? err.message : 'Failed to gift coins');
      setTimeout(() => setError(null), 5000);
    } finally {
      setGiftingCoins(false);
    }
  };

  const sortedAndFilteredUsers = React.useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    const filtered = users.filter(user =>
      (user.email?.toLowerCase().includes(searchLower)) ||
      (user.username?.toLowerCase().includes(searchLower)) ||
      (user.full_name?.toLowerCase().includes(searchLower))
    );

    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'username': {
          const nameA = (a.full_name || a.username || '').toLowerCase();
          const nameB = (b.full_name || b.username || '').toLowerCase();
          comparison = nameA.localeCompare(nameB);
          break;
        }
        case 'email':
          comparison = (a.email || '').toLowerCase().localeCompare((b.email || '').toLowerCase());
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [users, searchTerm, sortField, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedAndFilteredUsers.length / PAGE_SIZE));
  const paginatedUsers = sortedAndFilteredUsers.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleExportCSV = () => {
    if (sortedAndFilteredUsers.length === 0) {
      setError('No users to export');
      setTimeout(() => setError(null), 3000);
      return;
    }

    const headers = [
      'User_ID', 'Email', 'Username', 'Full_Name', 'Role',
      'Created_At', 'Last_Login', 'Avatar_URL',
      'Recordings_Count', 'Tournaments_Count', 'Achievements_Count'
    ];

    const rows = sortedAndFilteredUsers.map(user => [
      user.id,
      user.email,
      user.username,
      user.full_name || '',
      user.role,
      user.created_at,
      user.last_login || '',
      user.avatar_url || '',
      user.recordings_count || 0,
      user.tournaments_count || 0,
      user.achievements_count || 0
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell !== null && cell !== undefined ? String(cell).replace(/"/g, '""') : ''}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const timestamp = new Date().toISOString().split('T')[0];
    a.download = `users_export_${timestamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center mb-6">
        <Link to="/admin/dashboard" className="mr-4 text-white/70 hover:text-white transition-colors">
          <ArrowLeft size={20} />
        </Link>
        <h1 className="text-2xl font-bold">User Management</h1>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 text-red-500">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-6 text-green-500">
          {success}
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50" size={18} />
          <input
            type="text"
            placeholder="Search users..."
            className="w-full pl-10 pr-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-kod-accent text-white"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleExportCSV}
            className="btn-outline flex items-center"
            title="Export user list to CSV"
          >
            <Download size={18} className="mr-2" />
            Export CSV ({sortedAndFilteredUsers.length})
          </button>
          <button
            onClick={() => setShowPushModal(true)}
            className="btn-outline flex items-center"
            title="Send push notification"
          >
            <Bell size={18} className="mr-2" />
            Send Push
          </button>
          <button
            onClick={() => setEditingUser({})}
            className="btn-primary flex items-center"
          >
            <UserPlus size={18} className="mr-2" />
            Add New User
          </button>
        </div>
      </div>

      <div className="bg-kod-purple-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-kod-purple-700">
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider cursor-pointer select-none hover:text-white transition-colors"
                  onClick={() => handleSort('username')}
                >
                  <span className="inline-flex items-center">
                    User
                    <SortIcon field="username" />
                  </span>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider cursor-pointer select-none hover:text-white transition-colors"
                  onClick={() => handleSort('email')}
                >
                  <span className="inline-flex items-center">
                    Email
                    <SortIcon field="email" />
                  </span>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Role</th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider cursor-pointer select-none hover:text-white transition-colors"
                  onClick={() => handleSort('created_at')}
                >
                  <span className="inline-flex items-center">
                    Joined
                    <SortIcon field="created_at" />
                  </span>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-kod-purple-700">
              {paginatedUsers.map((user) => (
                <tr key={user.id} className="hover:bg-kod-purple-700/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {user.avatar_url ? (
                        <img
                          src={user.avatar_url}
                          alt={user.username}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                          <User size={16} className="text-white" />
                        </div>
                      )}
                      <div className="ml-3">
                        <div className="font-medium">{user.full_name || user.username}</div>
                        <div className="text-sm text-white/50">@{user.username}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{user.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={user.role}
                      onChange={(e) => handleUpdateRole(user.id, e.target.value as 'user' | 'admin')}
                      className="bg-kod-purple-700 border border-kod-purple-600 rounded px-2 py-1 text-sm"
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.last_login ? 'bg-green-900/30 text-green-300' : 'bg-yellow-900/30 text-yellow-300'
                    }`}>
                      {user.last_login ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setShowUserDetails(user.id)}
                        className="text-white/70 hover:text-white hover:bg-kod-purple-600 p-2 rounded-lg transition-colors"
                        title="View details"
                      >
                        <ChevronRight size={18} />
                      </button>
                      <button
                        onClick={() => setUserToDelete(user)}
                        className="text-white/70 hover:text-red-500 hover:bg-red-500/10 p-2 rounded-lg transition-colors"
                        title="Delete user"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-kod-purple-700">
            <p className="text-sm text-white/50">
              Showing {((currentPage - 1) * PAGE_SIZE) + 1}--{Math.min(currentPage * PAGE_SIZE, sortedAndFilteredUsers.length)} of {sortedAndFilteredUsers.length} users
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-kod-purple-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={18} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => {
                  if (totalPages <= 7) return true;
                  if (page === 1 || page === totalPages) return true;
                  if (Math.abs(page - currentPage) <= 1) return true;
                  return false;
                })
                .reduce<(number | 'ellipsis')[]>((acc, page, idx, arr) => {
                  if (idx > 0) {
                    const prev = arr[idx - 1];
                    if (page - prev > 1) acc.push('ellipsis');
                  }
                  acc.push(page);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === 'ellipsis' ? (
                    <span key={`e-${idx}`} className="px-2 text-white/30">...</span>
                  ) : (
                    <button
                      key={item}
                      onClick={() => setCurrentPage(item)}
                      className={`min-w-[2rem] h-8 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === item
                          ? 'bg-kod-accent text-white'
                          : 'text-white/60 hover:text-white hover:bg-kod-purple-600'
                      }`}
                    >
                      {item}
                    </button>
                  )
                )}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-kod-purple-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User Details Modal */}
      {showUserDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-kod-purple-800 rounded-lg p-6 max-w-2xl w-full">
            {users.find(u => u.id === showUserDetails) && (
              <>
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center">
                    {users.find(u => u.id === showUserDetails)?.avatar_url ? (
                      <img
                        src={users.find(u => u.id === showUserDetails)!.avatar_url}
                        alt="User"
                        className="w-16 h-16 rounded-full object-cover mr-4"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center mr-4">
                        <User size={28} className="text-white" />
                      </div>
                    )}
                    <div>
                      <h2 className="text-xl font-bold">{users.find(u => u.id === showUserDetails)?.full_name}</h2>
                      <p className="text-white/70">@{users.find(u => u.id === showUserDetails)?.username}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowUserDetails(null)}
                    className="text-white/70 hover:text-white p-2"
                  >
                    <XCircle size={24} />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="bg-kod-purple-700/50 p-4 rounded-lg">
                      <h3 className="font-medium mb-2">Account Information</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center text-white/70">
                          <Mail size={16} className="mr-2" />
                          {users.find(u => u.id === showUserDetails)?.email}
                        </div>
                        <div className="flex items-center text-white/70">
                          <Calendar size={16} className="mr-2" />
                          Joined {new Date(users.find(u => u.id === showUserDetails)?.created_at || '').toLocaleDateString()}
                        </div>
                        <div className="flex items-center text-white/70">
                          <Clock size={16} className="mr-2" />
                          Last active {users.find(u => u.id === showUserDetails)?.last_login ? 
                            new Date(users.find(u => u.id === showUserDetails)?.last_login || '').toLocaleDateString() : 
                            'Never'}
                        </div>
                      </div>
                    </div>

                    <div className="bg-kod-purple-700/50 p-4 rounded-lg">
                      <h3 className="font-medium mb-2">Activity Stats</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-kod-purple-700 p-3 rounded-lg">
                          <div className="flex items-center text-white/70 mb-1">
                            <Mic size={16} className="mr-2" />
                            Recordings
                          </div>
                          <div className="text-xl font-bold">
                            {users.find(u => u.id === showUserDetails)?.recordings_count || 0}
                          </div>
                        </div>
                        <div className="bg-kod-purple-700 p-3 rounded-lg">
                          <div className="flex items-center text-white/70 mb-1">
                            <Trophy size={16} className="mr-2" />
                            Tournaments
                          </div>
                          <div className="text-xl font-bold">
                            {users.find(u => u.id === showUserDetails)?.tournaments_count || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-kod-purple-700/50 p-4 rounded-lg">
                      <h3 className="font-medium mb-2">Recent Activity</h3>
                      <div className="space-y-3">
                        {/* Add recent activity items here */}
                        <div className="text-white/50 text-sm text-center py-4">
                          No recent activity to display
                        </div>
                      </div>
                    </div>

                    <div className="bg-kod-purple-700/50 p-4 rounded-lg">
                      <h3 className="font-medium mb-2">Achievements</h3>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Star size={16} className="text-yellow-400 mr-2" />
                            <span>Total Achievements</span>
                          </div>
                          <span className="font-bold">
                            {users.find(u => u.id === showUserDetails)?.achievements_count || 0}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-kod-purple-700/50 p-4 rounded-lg">
                      <h3 className="font-medium mb-2">Actions</h3>
                      <div className="space-y-2">
                        <button
                          onClick={() => setShowGiftCoinsModal(true)}
                          className="w-full bg-kod-accent hover:bg-kod-accent-dark text-white px-4 py-2 rounded-lg flex items-center justify-center transition-colors"
                        >
                          <Coins size={18} className="mr-2" />
                          Gift Coins
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-kod-purple-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">
                {editingUser.id ? 'Edit User' : 'Add New User'}
              </h2>
              <button
                onClick={() => {
                  setEditingUser(null);
                  setValidationErrors({});
                }}
                className="text-white/70 hover:text-white"
              >
                <XCircle size={24} />
              </button>
            </div>

            {Object.keys(validationErrors).length > 0 && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
                {Object.values(validationErrors).map((error, index) => (
                  <div key={index} className="text-red-500 text-sm">{error}</div>
                ))}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                  type="email"
                  value={editingUser.email || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  className={`input ${validationErrors.email ? 'border-red-500' : ''}`}
                  placeholder="user@example.com"
                />
                {validationErrors.email && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Username *</label>
                <input
                  type="text"
                  value={editingUser.username || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })}
                  className={`input ${validationErrors.username ? 'border-red-500' : ''}`}
                  placeholder="username"
                />
                {validationErrors.username && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.username}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Full Name *</label>
                <input
                  type="text"
                  value={editingUser.full_name || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, full_name: e.target.value })}
                  className={`input ${validationErrors.full_name ? 'border-red-500' : ''}`}
                  placeholder="First Last"
                />
                {validationErrors.full_name && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.full_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Role</label>
                <select
                  value={editingUser.role || 'user'}
                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value as 'user' | 'admin' })}
                  className="input"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => {
                  setEditingUser(null);
                  setValidationErrors({});
                }}
                className="btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveUser}
                className="btn-primary"
              >
                {editingUser.id ? 'Update User' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {userToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-kod-purple-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center mb-6">
              <div className="bg-red-500/10 p-3 rounded-full mr-4">
                <AlertTriangle className="text-red-500" size={24} />
              </div>
              <h2 className="text-xl font-bold">Delete User</h2>
            </div>
            
            <p className="text-white/70 mb-6">
              Are you sure you want to delete {userToDelete.full_name || userToDelete.username}? This action cannot be undone and will remove all associated data.
            </p>
            
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setUserToDelete(null)}
                className="btn-outline"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md transition-colors flex items-center"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Deleting...
                  </div>
                ) : (
                  <>
                    <Trash2 size={18} className="mr-2" />
                    Delete User
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gift Coins Modal */}
      {showGiftCoinsModal && showUserDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-kod-purple-800 rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-6">Gift Coins</h2>

            <div className="mb-4 p-3 bg-kod-purple-700/50 rounded-lg">
              <p className="text-sm text-white/70">Gifting coins to:</p>
              <p className="font-semibold">
                {users.find(u => u.id === showUserDetails)?.full_name} (@{users.find(u => u.id === showUserDetails)?.username})
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Amount</label>
                <input
                  type="number"
                  min="1"
                  className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-kod-accent"
                  value={giftAmount}
                  onChange={(e) => setGiftAmount(parseInt(e.target.value) || 0)}
                  placeholder="Enter amount"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Gift Type</label>
                <select
                  className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-kod-accent"
                  value={giftType}
                  onChange={(e) => setGiftType(e.target.value as typeof giftType)}
                >
                  <option value="gift">Gift</option>
                  <option value="reload">Reload</option>
                  <option value="promotion">Promotion</option>
                  <option value="compensation">Compensation</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Note (Optional)</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-kod-accent"
                  value={giftNote}
                  onChange={(e) => setGiftNote(e.target.value)}
                  placeholder="Optional note"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                className="px-4 py-2 bg-kod-purple-700 hover:bg-kod-purple-600 text-white rounded-md transition-colors"
                onClick={() => {
                  setShowGiftCoinsModal(false);
                  setGiftAmount(0);
                  setGiftNote('');
                }}
                disabled={giftingCoins}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 text-white rounded-md transition-colors ${
                  giftAmount <= 0 || giftingCoins
                    ? 'bg-kod-accent/50 cursor-not-allowed'
                    : 'bg-kod-accent hover:bg-kod-accent-dark'
                }`}
                onClick={handleGiftCoins}
                disabled={giftAmount <= 0 || giftingCoins}
              >
                {giftingCoins ? 'Gifting...' : 'Gift Coins'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPushModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-kod-purple-800 border border-kod-purple-600 rounded-xl shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-kod-purple-600">
              <div className="flex items-center gap-2">
                <Bell size={20} className="text-kod-accent" />
                <h3 className="text-lg font-bold">Send Push Notification</h3>
              </div>
              <button
                onClick={closePushModal}
                className="text-white/60 hover:text-white transition-colors"
                disabled={sendingPush}
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2">Audience</label>
                <div className="flex bg-kod-purple-900 rounded-lg p-1 border border-kod-purple-600">
                  <button
                    type="button"
                    onClick={() => setPushAudience('admins')}
                    disabled={sendingPush}
                    className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                      pushAudience === 'admins'
                        ? 'bg-kod-accent text-white shadow'
                        : 'text-white/60 hover:text-white'
                    }`}
                  >
                    <Shield size={14} />
                    Admins only (test)
                  </button>
                  <button
                    type="button"
                    onClick={() => setPushAudience('all')}
                    disabled={sendingPush}
                    className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                      pushAudience === 'all'
                        ? 'bg-kod-accent text-white shadow'
                        : 'text-white/60 hover:text-white'
                    }`}
                  >
                    <User size={14} />
                    All users
                  </button>
                </div>
                {pushAudience === 'all' && (
                  <p className="text-xs text-amber-400 mt-2 flex items-center gap-1">
                    <AlertTriangle size={12} />
                    This will be delivered to every user with an active push token.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <input
                  type="text"
                  value={pushTitle}
                  onChange={(e) => setPushTitle(e.target.value)}
                  placeholder="e.g. New tournament is live"
                  maxLength={80}
                  disabled={sendingPush}
                  className="w-full px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
                />
                <p className="text-xs text-white/40 mt-1">{pushTitle.length}/80</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Message</label>
                <textarea
                  value={pushBody}
                  onChange={(e) => setPushBody(e.target.value)}
                  placeholder="Write the notification body..."
                  rows={4}
                  maxLength={240}
                  disabled={sendingPush}
                  className="w-full px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent resize-none"
                />
                <p className="text-xs text-white/40 mt-1">{pushBody.length}/240</p>
              </div>

              {pushError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-400">
                  {pushError}
                </div>
              )}

              {pushResult && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-sm text-green-400">
                  Sent {pushResult.sent} of {pushResult.recipients} notifications.
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-kod-purple-600 bg-kod-purple-900/40">
              <button
                onClick={closePushModal}
                disabled={sendingPush}
                className="btn-outline"
              >
                {pushResult ? 'Close' : 'Cancel'}
              </button>
              <button
                onClick={handleSendPush}
                disabled={sendingPush || !pushTitle.trim() || !pushBody.trim()}
                className="btn-primary flex items-center gap-2"
              >
                <Send size={16} />
                {sendingPush ? 'Sending...' : pushAudience === 'admins' ? 'Send to Admins' : 'Send to All Users'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};